// Helpers de validação da FORMA dos comandos na fronteira HTTP.
//
// Cada agregado (src/comandos/<agregado>.ts) exporta um `validar…` que
// transforma o `unknown` de `req.body` num comando de verdade, ou lança um erro
// em português. Estes helpers são o que essas funções têm em comum — só a
// forma; as regras de negócio ficam no domínio, que já devolve erro amigável.

import { isDataISO, type DataISO } from "../domain/datas.ts";

export function exigirTexto(valor: unknown, campo: string): string {
  if (typeof valor !== "string" || valor === "") {
    throw new Error(`Campo obrigatório ausente ou vazio: ${campo}.`);
  }
  return valor;
}

export function exigirBooleano(valor: unknown, campo: string): boolean {
  if (typeof valor !== "boolean") {
    throw new Error(`Campo \`${campo}\` precisa ser true ou false.`);
  }
  return valor;
}

/**
 * Devolve `DataISO`, não `string`: é o que o type guard `isDataISO` já prova, e
 * é o que faz o comando declarar `DataISO` sem precisar de um `as` (ADR-0013).
 */
export function exigirData(valor: unknown, campo: string): DataISO {
  if (typeof valor === "string" && isDataISO(valor)) return valor;
  throw new Error(`Campo \`${campo}\` precisa ser uma data AAAA-MM-DD.`);
}

/**
 * Texto opcional: string como veio, `""` para o resto. Use para campos que o
 * domínio já valida (nome, descrição, motivo…) — assim a mensagem amigável do
 * domínio ("Informe o nome…") aparece em vez da genérica da fronteira.
 */
export function texto(valor: unknown): string {
  return typeof valor === "string" ? valor : "";
}

/** Um valor de um conjunto fechado (enum). Use quando o domínio NÃO valida. */
export function umDe<T extends string>(
  valor: unknown,
  campo: string,
  opcoes: readonly T[],
): T {
  if (typeof valor === "string" && (opcoes as readonly string[]).includes(valor)) {
    return valor as T;
  }
  throw new Error(`Campo \`${campo}\` precisa ser um de: ${opcoes.join(", ")}.`);
}

/**
 * Número a partir de um `unknown` (aceita "30" vindo de um `<input>`). Devolve
 * `NaN` para o que não é número — o domínio recusa `NaN` com mensagem amigável,
 * e assim a fronteira nunca **inventa** um valor (ex.: 0) por um campo quebrado.
 */
export function paraNumero(valor: unknown): number {
  const n = Number(valor);
  return Number.isFinite(n) ? n : NaN;
}

/**
 * Uma lista a partir de um `unknown`. `null`/ausente → `[]`; não-lista → erro.
 * `item` valida cada elemento e recebe o índice para compor a mensagem.
 */
export function lista<T>(
  valor: unknown,
  campo: string,
  item: (x: unknown, i: number) => T,
): T[] {
  if (valor == null) return [];
  if (!Array.isArray(valor)) {
    throw new Error(`\`${campo}\` precisa ser uma lista.`);
  }
  return valor.map(item);
}

/** Como `lista`, mas `null`/ausente também é erro — o campo é obrigatório. */
export function exigirLista<T>(
  valor: unknown,
  campo: string,
  item: (x: unknown, i: number) => T,
): T[] {
  if (!Array.isArray(valor)) {
    throw new Error(`\`${campo}\` precisa ser uma lista.`);
  }
  return valor.map(item);
}

/** Confere que `x` é um objeto e devolve como dicionário; senão, erro. */
export function comoObjeto(x: unknown, oQueEra: string): Record<string, unknown> {
  if (!x || typeof x !== "object") {
    throw new Error(`${oQueEra}: forma inválida.`);
  }
  return x as Record<string, unknown>;
}
